/* ============
 * Assignment Transformer
 * ============
 *
 * The transformer for the Assignment.
 */

import type {
  AssignmentCreationData,
  AssignmentResponseData,
  AssignmentType,
  StatusType,
} from '@/types/Models/Assignment'
import type { Assignment } from '@/types/Models/Assignment'
import { toRaw } from 'vue'
import { ADDRESS_DELIVERY, SERVICE_POINT_DELIVERY } from '@/const/AssignmentDeliveryTypes'
import { SINGLE } from '@/const/AssignmentTypes'
import { PACKAGE, PARCEL } from '@/const/DistributionChannels'
import ItemTransformer from '@/transformers/ItemTransformer'

export default class AssignmentTransformer {
  /**
   * Method used to transform a fetched Assignment.
   */
  static fetch(data: AssignmentResponseData): Assignment {
    const assignment: Assignment = {
      ...data,
      type: data.type as AssignmentType,
      status: data.status as StatusType,
      item: data.item ? ItemTransformer.fetch(data.item) : null,
      sourceFiles: data.sourceFiles || [],
      optimisationReportUrl: data.optimisationReportUrl || null,
      customerReference: data.customerReference || null,
      hasCustomPrices: data.hasCustomPrices || false,
      hasCustomPriorities: data.hasCustomPriorities || false,
      archived: data.archived || false,
      denyLabelsDownload: data.denyLabelsDownload || false,
    }

    return { ...data, ...assignment }
  }

  static create(assignmentData: AssignmentCreationData, isStaff: boolean) {
    const data = { ...toRaw(assignmentData) } as any
    if (!data?.sender_address_id) {
      delete data.sender_address_id
    }
    if (!isStaff) {
      delete data.company_id
    }

    // transform item
    data.item = ItemTransformer.create(data.item)

    if (assignmentData.assignment_type === SINGLE) {
      delete data.file
      if (assignmentData.distribution_channel === PARCEL && data.item) {
        // move this hack to backend
        data.item.delivery_type = data.item.company_name ? ADDRESS_DELIVERY : SERVICE_POINT_DELIVERY
      }

      if (assignmentData.distribution_channel === PACKAGE) {
        data.pickup_date = null
      }

      data.optimize = true
      data.create_report = false
      data.create_labels = false
      return data
    }

    // For consolidated assignment
    delete data.item
    const buildFormData = (formData: FormData, data: any, parentKey: string = '') => {
      if (data && typeof data === 'object' && !(data instanceof File)) {
        Object.keys(data).forEach((key) => {
          buildFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key)
        })
      } else {
        const value = data == null ? '' : data
        formData.append(parentKey, value)
      }
    }

    const formData = new FormData()

    buildFormData(formData, data)

    if (!data.file) {
      formData.delete('file')
    }
    return formData
  }
}
